import {
    IConfigLog,
    IConfigLogDestination,
    IConfigLogLevel,
    injectConfigLog,
} from '../interface/config';
import { ILog } from '../interface/log';
import consoleLoggerInstantiator from './log.console';
import aiLoggerInstantiator from './log.ai';
import { ILogger, LOG_LEVEL } from './log.if';

/* IConfigLogLevel & LOG_LEVEL have same enum values, just cast */
const LogLevelToConfigLogLevel = (level: LOG_LEVEL): IConfigLogLevel => {
    return level as number;
};
const ConfigLogLevelsToLogLevels = (level: IConfigLogLevel[]): LOG_LEVEL[] => {
    return level as number[];
};

function _isLogLevelEnabled(
    config: IConfigLog | Array<IConfigLogLevel>,
    logLevel: LOG_LEVEL,
): boolean {
    if (Array.isArray(config)) {
        const configArr = config as Array<IConfigLogLevel>;
        return configArr.includes(LogLevelToConfigLogLevel(logLevel));
    } else {
        const configObj = config as IConfigLog;
        return configObj.loggingLevelsEnabled.includes(LogLevelToConfigLogLevel(logLevel));
    }
}

function logger(config: IConfigLog): ILog {
    const loggers: Array<ILogger> = [];

    const consoleLogEnabled = config.outputDestinations.includes(IConfigLogDestination.Console);
    const consoleConfig = config.outputConfiguration[IConfigLogDestination.Console] ?? {};
    if (consoleLogEnabled) {
        loggers.push(
            consoleLoggerInstantiator(
                ConfigLogLevelsToLogLevels(config.loggingLevelsEnabled),
                consoleConfig,
            ),
        );
    }

    const remoteAIEnabled = config.outputDestinations.includes(
        IConfigLogDestination.Remote_ApplicationInsights,
    );
    const remoteAIConfig =
        config.outputConfiguration[IConfigLogDestination.Remote_ApplicationInsights] ?? {};
    if (remoteAIEnabled) {
        loggers.push(
            aiLoggerInstantiator(
                ConfigLogLevelsToLogLevels(config.loggingLevelsEnabled),
                remoteAIConfig,
            ),
        );
    }

    function log(logLevel: LOG_LEVEL, message: string) {
        for (let i = 0; i < loggers.length; i++) {
            loggers[i].log(logLevel, message);
        }
    }

    const milestone = (msg: any) => log(LOG_LEVEL.MILESTONE, msg);
    const verbose = (msg: any) => log(LOG_LEVEL.VERBOSE, msg);
    const trace = (msg: any) => log(LOG_LEVEL.TRACE, msg);
    const info = (msg: any) => log(LOG_LEVEL.INFO, msg);
    const warn = (msg: any) => log(LOG_LEVEL.WARN, msg);
    const warning = (msg: any) => log(LOG_LEVEL.WARN, msg);
    const error = (msg: any) => log(LOG_LEVEL.ERROR, msg);
    const fatal = (msg: any) => log(LOG_LEVEL.FATAL, msg);
    const noop = (_: any) => {};

    const module: ILog = {
        milestone: _isLogLevelEnabled(config, LOG_LEVEL.MILESTONE) ? milestone : noop,
        verbose: _isLogLevelEnabled(config, LOG_LEVEL.VERBOSE) ? verbose : noop,
        trace: _isLogLevelEnabled(config, LOG_LEVEL.TRACE) ? trace : noop,
        info: _isLogLevelEnabled(config, LOG_LEVEL.INFO) ? info : noop,
        warn: _isLogLevelEnabled(config, LOG_LEVEL.WARN) ? warn : noop,
        warning: _isLogLevelEnabled(config, LOG_LEVEL.WARN) ? warning : noop,
        error: _isLogLevelEnabled(config, LOG_LEVEL.ERROR) ? error : noop,
        fatal: _isLogLevelEnabled(config, LOG_LEVEL.FATAL) ? fatal : noop,
    };

    return module;
}

/* At time of binding IConfigLog is not available, as the utils are binded first, before the config files. This should be refactored to remove the injectConfigLog() call below */
function lazy_logger(): ILog {
    let log: ILog | null = null;

    function instantiateLogger() {
        if (!log) {
            log = logger(injectConfigLog());
        }
    }

    const milestone = (msg: any) => {
        instantiateLogger();
        log?.milestone?.(msg);
    };
    const verbose = (msg: any) => {
        instantiateLogger();
        log?.verbose(msg);
    };
    const trace = (msg: any) => {
        instantiateLogger();
        log?.trace(msg);
    };
    const info = (msg: any) => {
        instantiateLogger();
        log?.info(msg);
    };
    const warn = (msg: any) => {
        instantiateLogger();
        log?.warn(msg);
    };
    const warning = (msg: any) => {
        instantiateLogger();
        log?.warning(msg);
    };
    const error = (msg: any) => {
        instantiateLogger();
        log?.error(msg);
    };
    const fatal = (msg: any) => {
        instantiateLogger();
        log?.fatal(msg);
    };

    const module: ILog = {
        milestone,
        verbose,
        trace,
        info,
        warn,
        warning,
        error,
        fatal,
    };

    return module;
}

export default lazy_logger;
